import { z } from 'zod';
import { ClinicianRole } from '../programs/schema';
import { apiEnvelope } from '../envelope';
import { careTeamPhotoUrlShape } from './care-photo-url';
import { welcomePoolPreviewSchema } from './welcome-pool-preview';

/**
 * Wire contract for the member-facing care-team presentation — the payload of
 * `GET /api/v1/members/me/care-team`, and the only description of it.
 *
 * One schema owns the shape on both sides of the wire: the members route
 * contract derives its response from it and the mobile feature service parses
 * with it, so the server projection and the client parser cannot drift. The
 * route is named here deliberately: a sibling module once carried a second,
 * unreachable payload shape whose docblocks claimed the same route, so anyone
 * grepping for the path found the draft rather than the contract being served.
 *
 * The projection is presentation-only by construction: the strict objects
 * admit no internal-user id, email, or Cognito subject, and `fallback` carries
 * NO identity fields at all — it tells the client to render its own bundled
 * demo identity (the one explicit fallback seam; the fallback avatar is a
 * bundled client asset the server cannot author).
 */
/**
 * The one owner of the renderable clinician-photo grammar: an https URL whose
 * authority is dot-separated host labels of [A-Za-z0-9-] with an optional
 * numeric port. A regex, not the URL parser — `new URL()` silently reparses
 * hostless values like "https:///x" and junk ports like
 * "https://example.com:bad". Both the wire schema below and the server-side
 * presentation service validate against this same shape, so the profile and
 * home surfaces cannot disagree about which stored photo values render.
 */
export { careTeamPhotoUrlShape, isRenderableCareTeamPhotoUrl } from './care-photo-url';

export const careTeamSecondaryPresentationSchema = z
  .object({
    specialistId: z.string().uuid(),
    name: z.string().min(1),
    initials: z.string().min(1),
    role: ClinicianRole,
    photoUrl: z
      .string()
      .regex(careTeamPhotoUrlShape, 'photoUrl must be an https URL with a host')
      .optional(),
  })
  .strict();
export type CareTeamSecondaryPresentation = z.infer<typeof careTeamSecondaryPresentationSchema>;

export const careTeamPresentationSchema = z.discriminatedUnion('source', [
  z
    .object({
      source: z.literal('derived'),
      /** The clinician's picked display name (preferred → first → full). */
      name: z.string().min(1),
      /** Server-derived avatar initials for the photo-less fallback rendering. */
      initials: z.string().min(1),
      /** Routable specialist id for the primary assignment — scopes profile booking. */
      specialistId: z.string().uuid().optional(),
      /**
       * The clinician's photo as a renderable URL (ADR-073 media ref), present
       * only when the row carries a valid stored photo. Bound to the shared
       * photo grammar above so a malformed stored value fails the parse on the
       * client, which resolves to the fallback identity instead of rendering a
       * broken avatar URI.
       */
      photoUrl: z
        .string()
        .regex(careTeamPhotoUrlShape, 'photoUrl must be an https URL with a host')
        .optional(),
      /** Open non-primary assignments, each with a routable specialist id. */
      secondaries: z.array(careTeamSecondaryPresentationSchema).optional(),
      /**
       * Who hosts the member's next call — before intake, the welcome-call
       * pool — led by the member's own specialist when they are in it. Absent
       * when the pool could not be read.
       */
      welcomePoolPreview: welcomePoolPreviewSchema.optional(),
    })
    .strict(),
  z.object({ source: z.literal('fallback') }).strict(),
  /**
   * The member has no open care-team assignment yet. Carries no identity
   * fields by construction — the client renders its generic meet-your-care-team
   * presentation, never a named specialist. Unknown keys are stripped rather
   * than rejected, so a reader never fails on a field the server adds, and an
   * identity-shaped key can never reach the client model.
   */
  z
    .object({
      source: z.literal('unassigned'),
      welcomePoolPreview: welcomePoolPreviewSchema.optional(),
      /**
       * The default pool new members are assigned to — the people who answer a
       * new member's messages — with the member's own provisional specialist
       * first. Same anonymous face shape as the welcome pool.
       */
      defaultPoolPreview: welcomePoolPreviewSchema.optional(),
    })
    .strip(),
]);
export type CareTeamPresentation = z.infer<typeof careTeamPresentationSchema>;

export const careTeamPresentationResponseSchema = apiEnvelope(careTeamPresentationSchema);
export type CareTeamPresentationResponse = z.infer<typeof careTeamPresentationResponseSchema>;
